import {
  PrismLight as SyntaxHighlighter,
  type SyntaxHighlighterProps,
} from "react-syntax-highlighter";
import { useColorScheme } from "@mui/material";
import cx from "classnames";
import coy from "react-syntax-highlighter/dist/esm/styles/prism/coy";
import { CopyText } from "src/components/features/small/CopyText/CopyText";
import {
  CUSTOM_STYLE,
  DARK_THEME_NO_BACKGROUND,
  SENTIMENTS_COLORS,
} from "./CodeSnippet.constants";

type CodeSnippetProps = Omit<SyntaxHighlighterProps, "children"> & {
  title?: string | React.ReactNode;
  code: string;
  className?: string;
  sentiment?: "positive" | "negative" | "neutral";
  withoutCopy?: boolean;
};

const CodeSnippet = ({
  title,
  code,
  className,
  sentiment = "neutral",
  withoutCopy = false,
  withoutSyntaxHighlighting = false,
  ...props
}: CodeSnippetProps) => {
  const { mode } = useColorScheme();
  const theme = mode === "dark" ? DARK_THEME_NO_BACKGROUND : coy;
  const compColors = SENTIMENTS_COLORS[sentiment];

  return (
    <div
      className={cx(
        "flex flex-col border rounded-sm relative overflow-hidden",
        compColors.border,
        className
      )}
    >
      {title ? (
        <header
          className={cx(
            "flex justify-between items-center p-3 border-b",
            compColors.background,
            compColors.border
          )}
        >
          <h3>{title}</h3>
          {!withoutCopy && <CopyText displayText="Copy" text={code} />}
        </header>
      ) : (
        !withoutCopy && (
          <CopyText
            text={code}
            buttonOnly
            className="absolute z-1 right-1 bottom-1"
          />
        )
      )}

      <section className={cx("flex-1 p-3 overflow-auto", compColors.background)}>
        {withoutSyntaxHighlighting ? (
          // Bypass PrismLight entirely. SyntaxHighlighter with language="text"
          // still tokenizes and emits a React node per token, which becomes a
          // bottleneck for large strings (multi-MB tool result bodies). Plain
          // <pre> is O(1) regardless of content size.
          <pre
            style={CUSTOM_STYLE}
            className="font-mono whitespace-pre-wrap wrap-break-word text-xs"
          >
            {code}
          </pre>
        ) : (
          <SyntaxHighlighter
            codeTagProps={{ style: { fontFamily: "monospace" } }}
            // codeTagProps={{ style: { fontFamily: "inherit" } }}
            customStyle={CUSTOM_STYLE}
            language="bash"
            style={theme}
            wrapLongLines={false}
            {...props}
          >
            {code}
          </SyntaxHighlighter>
        )}
      </section>
    </div>
  );
};

export default CodeSnippet;
